import { useState, useEffect } from "react"
import Header from "../common/Header.js"
import Sidebar from "../common/Sidebar.js"
import dashboardService from "../../services/DashboardService.js"
import userService from "../../services/UserService.js"
import Alert from "../common/Alert.js"
import {
  FileText, TrendingUp, Filter, X, ChevronDown,
  DollarSign, CreditCard, AlertCircle, CheckCircle2,
  Clock, Download, BarChart3, Activity, IndianRupee, ShieldCheck,
  Box, ClipboardCheck, Package, Receipt, Users, LayoutDashboard,
  Landmark, Percent
} from "lucide-react"
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell,
  LineChart, Line
} from 'recharts'

const COLORS = ['#F97316', '#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#14B8A6']

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl shadow-xl p-3 text-sm">
        <p className="font-semibold text-gray-700 mb-2">{label}</p>
        {payload.map((p, i) => (
          <div key={i} className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full" style={{ background: p.color }} />
            <span className="text-gray-600">{p.name}:</span>
            <span className="font-bold text-gray-900">{p.value}</span>
          </div>
        ))}
      </div>
    )
  }
  return null
}

const StatCard = ({ title, value, subtitle, icon: Icon, gradient, textLight }) => (
  <div className={`${gradient} rounded-md shadow-lg p-5 py-3 text-white relative overflow-hidden`}>
    <div className="absolute top-0 right-0 w-32 h-32 rounded-full bg-white opacity-5 -translate-y-8 translate-x-8" />
    <div className="relative">
      <div className="flex items-center justify-between mb-4">
        <p className={`${textLight} text-sm font-medium`}>{title}</p>
        <div className="bg-white bg-opacity-20 p-2.5 rounded-xl">
          <Icon size={22} />
        </div>
      </div>
      <h3 className="text-3xl font-bold">{value}</h3>
      {subtitle && <p className={`${textLight} text-xs mt-1`}>{subtitle}</p>}
    </div>
  </div>
)

const SectionHeader = ({ icon: Icon, title, color = "text-orange" }) => (
  <div className="flex items-center gap-2 mb-4 bg-gray-100 p-2 px-1">
    <div className="w-1 h-6 rounded-full bg-orange-500" />
    <Icon size={20} className={color} />
    <h2 className="text-lg font-semibold text-gray-600">{title}</h2>
  </div>
)

const AccountantDashboard = () => {
  const [loading, setLoading] = useState(true)
  const [dashboardData, setDashboardData] = useState(null)
  const [clients, setClients] = useState([])
  const [filters, setFilters] = useState({ from_date: '', to_date: '', client_id: '' })
  const [showFilters, setShowFilters] = useState(false)
  const [datePreset, setDatePreset] = useState('')

  useEffect(() => {
    const userRole = dashboardService.getCurrentUserRole()
    const userId = dashboardService.getCurrentUserId()
    console.log('[v0] AccountantDashboard initialized - Role:', userRole, '- User ID:', userId)
    
    fetchDashboardData()
    fetchClients()
  }, [])

  const fetchDashboardData = async (customFilters = filters) => {
    try {
      setLoading(true)
      const response = await dashboardService.getDashboardData(customFilters)
      setDashboardData(response.data)
    } catch (err) {
      Alert.error('Failed to load dashboard data')
    } finally {
      setLoading(false)
    }
  }

  const fetchClients = async () => {
    try {
      const response = await userService.getClients(1, 100)
      setClients(response.data || [])
    } catch (err) {
      console.error('Failed to load clients', err)
    }
  }

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }))
    if (field !== 'client_id') setDatePreset('')
  }

  const handleDatePresetChange = (preset) => {
    if (!preset) { clearFilters(); return }
    const presets = dashboardService.getDatePresets()
    const selected = presets[preset]
    if (selected) {
      const newFilters = { ...filters, from_date: selected.from, to_date: selected.to }
      setFilters(newFilters)
      setDatePreset(preset)
      fetchDashboardData(newFilters)
    }
  }

  const applyFilters = () => { fetchDashboardData(); setShowFilters(false) }

  const clearFilters = () => {
    const cleared = { from_date: '', to_date: '', client_id: '' }
    setFilters(cleared)
    setDatePreset('')
    fetchDashboardData(cleared)
  }

  const exportBillingReport = () => {
    const b = dashboardData?.billing
    if (!b) return
    const csv = [
      ['Metric', 'Value'],
      ['Total Invoices', b.total],
      ['Paid', b.paid],
      ['Unpaid', b.unpaid],
      ['Partial Paid', b.partial_paid],
      ['Total Billed Amount', b.total_billed_amount],
      ['Total Paid Amount', b.total_paid_amount],
      ['Outstanding Balance', b.total_balance_amount],
    ].map(r => r.join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = 'billing_report.csv'; a.click()
    URL.revokeObjectURL(url)
  }

  const exportTIReport = () => {
    const ti = dashboardData?.ti_report
    if (!ti) return
    const csv = [
      ['Metric', 'Value'],
      ['Total TI Reports', ti.total],
      ['Published', ti.published],
      ['Draft', ti.unpublished],
    ].map(r => r.join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = 'ti_report_summary.csv'; a.click()
    URL.revokeObjectURL(url)
  }

  const hasActiveFilters = filters.from_date || filters.to_date || filters.client_id

  // ─── Chart data ─────────────────────────────────────────────────────────
  const prepareAreaChartData = () => {
    if (!dashboardData?.records_by_date) return []
    const dateMap = new Map()
    Object.entries(dashboardData.records_by_date).forEach(([key, data]) => {
      data.forEach(item => {
        if (!dateMap.has(item.date)) dateMap.set(item.date, { date: item.date })
        const label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
        dateMap.get(item.date)[label] = item.count
      })
    })
    return Array.from(dateMap.values()).sort((a, b) => new Date(a.date) - new Date(b.date))
  }

  const prepareRecordTypesChart = () => {
    if (!dashboardData) return []
    return [
      { name: 'Dry Box', value: dashboardData.dry_box_survey?.total || 0 },
      { name: 'Tank Cleaning', value: dashboardData.tank_cleaning?.total || 0 },
      { name: 'On Hire', value: dashboardData.onhire?.total || 0 },
      { name: 'EI Receipt', value: dashboardData.ei_receipt?.total || 0 },
      { name: 'TI Report', value: dashboardData.ti_report?.total || 0 },
    ]
  }

  const prepareBillingTrend = () => {
    if (!dashboardData?.records_by_date?.billing) return []
    return dashboardData.records_by_date.billing.map(item => ({ date: item.date, invoices: item.count }))
  }

  const prepareBillingStatusData = () => {
    const b = dashboardData?.billing
    if (!b) return []
    return [
      { name: 'Paid', value: b.paid, fill: '#10B981' },
      { name: 'Unpaid', value: b.unpaid, fill: '#EF4444' },
      { name: 'Partial', value: b.partial_paid, fill: '#F59E0B' },
    ]
  }

  const prepareTIStatusData = () => {
    const ti = dashboardData?.ti_report
    if (!ti) return []
    return [
      { name: 'Published', value: ti.published, fill: '#10B981' },
      { name: 'Draft', value: ti.unpublished, fill: '#94A3B8' },
    ]
  }

  const preparePaymentStatusBar = () => {
    const ps = dashboardData?.payment_status_summary
    if (!ps) return []
    return Object.entries(ps).map(([svc, stats]) => ({
      name: svc.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      paid: stats.paid,
      pending: stats.pending,
      unpaid: stats.unpaid,
    }))
  }

  const billing = dashboardData?.billing || {}
  const ti = dashboardData?.ti_report || {}
  const totalRecords = (dashboardData?.dry_box_survey?.total || 0) + (dashboardData?.tank_cleaning?.total || 0) +
    (dashboardData?.onhire?.total || 0) + (dashboardData?.ei_receipt?.total || 0) + (dashboardData?.ti_report?.total || 0)

  if (loading && !dashboardData) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-orange-500 mx-auto mb-4" />
          <p className="text-gray-500 font-medium">Loading dashboard…</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-[1850px] mx-auto">
      <Header />
      <div className="flex">
        <Sidebar />
        <div className="flex bg-gray-50 w-full">
          <div className="flex-1 flex flex-col">
            <main className="flex-1 p-6 overflow-auto">

              {/* ── Page header ── */}
              <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
                <div>
                  <h1 className="text-2xl sm:text-2xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
                    <LayoutDashboard className="text-orange" size={28} />
                    Accountant Dashboard
                  </h1>
                </div>
                <div className="flex gap-3">
                  <div className="relative">
                    <select value={datePreset} onChange={e => handleDatePresetChange(e.target.value)}
                      className="pl-3 pr-9 py-2 border border-gray-200 rounded-sm text-sm focus:ring-2 focus:ring-orange-500 bg-white appearance-none">
                      <option value="">All Time</option>
                      {['today','yesterday','last7Days','last30Days','last3Months','last6Months','thisMonth','thisYear','lastYear'].map(k => (
                        <option key={k} value={k}>{dashboardService.getDatePresets()[k]?.label}</option>
                      ))}
                    </select>
                    <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                  </div>
                  <button onClick={() => setShowFilters(!showFilters)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-medium transition-all ${
                      hasActiveFilters ? 'bg-orange-500 text-white shadow-md' : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                    }`}>
                    <Filter size={16} />
                    Filters
                    {hasActiveFilters && <span className="bg-white text-orange-500 px-1.5 py-0.5 rounded-full text-xs font-bold">{[filters.from_date, filters.to_date, filters.client_id].filter(Boolean).length}</span>}
                  </button>
                </div>
              </div>

              {/* ── Filter panel ── */}
              {showFilters && (
                <div className="bg-white rounded-sm shadow-sm border border-gray-100 p-6 mb-8">
                  <div className="flex items-center justify-between mb-5">
                    <h3 className="font-semibold text-gray-500">Advanced Filters</h3>
                    <button onClick={() => setShowFilters(false)} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
                    {[
                      { label: 'From Date', el: <input type="date" value={filters.from_date} onChange={e => handleFilterChange('from_date', e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent" /> },
                      { label: 'To Date', el: <input type="date" value={filters.to_date} onChange={e => handleFilterChange('to_date', e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent" /> },
                      { label: 'Client', el: (
                        <select value={filters.client_id} onChange={e => handleFilterChange('client_id', e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent">
                          <option value="">All Clients</option>
                          {clients.map(c => <option key={c.user_id} value={c.user_id}>{c.name} ({c.organization_name || 'N/A'})</option>)}
                        </select>
                      )},
                    ].map(({ label, el }) => (
                      <div key={label}>
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">{label}</label>
                        {el}
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-3 justify-end">
                    <button onClick={clearFilters} className="px-4 py-2 border border-gray-200 text-gray-600 rounded-xl text-sm hover:bg-gray-50">Clear All</button>
                    <button onClick={applyFilters} className="px-4 py-2 bg-buttonBg text-white rounded-xl text-sm hover:bg-orange-600 font-medium">Apply Filters</button>
                  </div>
                </div>
              )}

              {/* ══════════════════════════════════════
                  SECTION 1 – BILLING
              ══════════════════════════════════════ */}
              <SectionHeader icon={IndianRupee} title="Billing & Financial Overview" color="text-green-600" />

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-4">
                <StatCard title="Total Invoices" value={billing.total || 0} subtitle="All statuses" icon={FileText} gradient="bg-gradient-to-br from-slate-600 to-slate-700" textLight="text-slate-300" />
                <StatCard title="Total Billed" value={`₹${(billing.total_billed_amount || 0).toLocaleString()}`} subtitle="Gross revenue" icon={CreditCard} gradient="bg-gradient-to-br from-blue-600 to-blue-700" textLight="text-blue-200" />
                <StatCard title="Total Collected" value={`₹${(billing.total_paid_amount || 0).toLocaleString()}`} subtitle="Payments received" icon={CheckCircle2} gradient="bg-gradient-to-br from-green-600 to-green-700" textLight="text-green-200" />
                <StatCard title="Outstanding" value={`₹${(billing.total_balance_amount || 0).toLocaleString()}`} subtitle="Balance due" icon={AlertCircle} gradient="bg-gradient-to-br from-red-500 to-red-600" textLight="text-red-100" />
              </div>

              {/* TDS + Bank Payment breakdown */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
                <StatCard title="Total Bank Payment" value={`₹${(billing.total_bank_payment || 0).toLocaleString()}`} subtitle="Received via bank" icon={Landmark} gradient="bg-gradient-to-br from-indigo-500 to-indigo-700" textLight="text-indigo-200" />
                <StatCard title="Total TDS" value={`₹${(billing.total_tds || 0).toLocaleString()}`} subtitle="Tax deducted at source" icon={Percent} gradient="bg-gradient-to-br from-purple-500 to-purple-700" textLight="text-purple-200" />
              </div>

              {/* Invoice status counts */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                {[
                  { label: 'Paid Invoices', count: billing.paid || 0, bg: 'bg-green-50 border-green-200', text: 'text-green-700', dot: 'bg-green-500' },
                  { label: 'Partially Paid', count: billing.partial_paid || 0, bg: 'bg-yellow-50 border-yellow-200', text: 'text-yellow-700', dot: 'bg-yellow-500' },
                  { label: 'Unpaid', count: billing.unpaid || 0, bg: 'bg-red-50 border-red-200', text: 'text-red-700', dot: 'bg-red-500' },
                ].map(({ label, count, bg, text, dot }) => (
                  <div key={label} className={`border rounded-sm p-5 ${bg}`}>
                    <div className={`flex items-center gap-2 mb-1 ${text}`}>
                      <div className={`w-2 h-2 rounded-full ${dot}`} />
                      <span className="text-xs font-semibold uppercase tracking-wide">{label}</span>
                    </div>
                    <p className={`text-3xl font-bold ${text}`}>{count}</p>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                <div className="lg:col-span-2 bg-white rounded-sm shadow-sm border border-gray-100 p-6">
                  <div className="flex items-center justify-between mb-5">
                    <h3 className="font-semibold text-gray-500">Monthly Billing Trend</h3>
                    <button onClick={exportBillingReport} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">
                      <Download size={13} /> Export CSV
                    </button>
                  </div>
                  <ResponsiveContainer width="100%" height={260}>
                    <AreaChart data={prepareBillingTrend()} margin={{ top: 5, right: 10, left: 0, bottom: 60 }}>
                      <defs>
                        <linearGradient id="gradBill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#10B981" stopOpacity={0.2}/>
                          <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} angle={-40} textAnchor="end" height={70} tickLine={false} />
                      <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                      <Tooltip content={<CustomTooltip />} />
                      <Area type="monotone" dataKey="invoices" stroke="#10B981" strokeWidth={2.5} fill="url(#gradBill)" dot={{ r: 3, fill: '#10B981' }} name="Invoices" connectNulls={true} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                <div className="bg-white rounded-sm shadow-sm border border-gray-100 p-6">
                  <h3 className="font-semibold text-gray-500 mb-4">Invoice Status Mix</h3>
                  <ResponsiveContainer width="100%" height={170}>
                    <PieChart>
                      <Pie data={prepareBillingStatusData()} cx="50%" cy="50%" innerRadius={45} outerRadius={75} paddingAngle={4} dataKey="value">
                        {prepareBillingStatusData().map((d, i) => <Cell key={i} fill={d.fill} />)}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="space-y-3 mt-4">
                    {prepareBillingStatusData().map(d => (
                      <div key={d.name} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ background: d.fill }} />
                          <span className="text-sm text-gray-600">{d.name}</span>
                        </div>
                        <span className="text-sm font-bold text-gray-800">{d.value}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-5 pt-5 border-t border-gray-100">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-500">Collection Rate</span>
                      <span className="font-bold text-gray-900">
                        {billing.total_billed_amount > 0 ? `${((billing.total_paid_amount / billing.total_billed_amount) * 100).toFixed(1)}%` : '0%'}
                      </span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2">
                      <div className="bg-green-500 h-2 rounded-full" style={{ width: billing.total_billed_amount > 0 ? `${Math.min((billing.total_paid_amount / billing.total_billed_amount) * 100, 100)}%` : '0%' }} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment status per service */}
              {preparePaymentStatusBar().length > 0 && (
                <div className="bg-white rounded-sm shadow-sm border border-gray-100 p-6 mb-8">
                  <h3 className="font-semibold text-gray-500 mb-5">Payment Status by Service Type</h3>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={preparePaymentStatusBar()} barCategoryGap="25%">
                      <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} tickLine={false} />
                      <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend iconType="circle" iconSize={8} />
                      <Bar dataKey="paid" fill="#10B981" name="Paid" radius={[4,4,0,0]} />
                      <Bar dataKey="pending" fill="#F59E0B" name="Pending" radius={[4,4,0,0]} />
                      <Bar dataKey="unpaid" fill="#EF4444" name="Unpaid" radius={[4,4,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* ══════════════════════════════════════
                  SECTION 3 – RECORDS OVERVIEW
              ══════════════════════════════════════ */}
              <SectionHeader icon={BarChart3} title="Records Overview" />

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
                {[
                  { label: 'Dry Box Survey', total: dashboardData?.dry_box_survey?.total, pub: dashboardData?.dry_box_survey?.published, icon: FileText, color: 'text-orange-500', border: 'border-orange-500' },
                  { label: 'Tank Cleaning', total: dashboardData?.tank_cleaning?.total, pub: dashboardData?.tank_cleaning?.published, icon: ClipboardCheck, color: 'text-blue-500', border: 'border-blue-500' },
                  { label: 'On Hire', total: dashboardData?.onhire?.total, pub: dashboardData?.onhire?.published, icon: Package, color: 'text-green-500', border: 'border-green-500' },
                  { label: 'EI Receipt', total: dashboardData?.ei_receipt?.total, pub: dashboardData?.ei_receipt?.published, icon: Receipt, color: 'text-purple-500', border: 'border-purple-500' },
                  { label: 'TI Report', total: dashboardData?.ti_report?.total, pub: dashboardData?.ti_report?.published, icon: ShieldCheck, color: 'text-teal-500', border: 'border-teal-500' },
                ].map(({ label, total, pub, icon: Icon, color, border }) => (
                  <div key={label} className={`bg-white rounded-sm shadow-sm border border-gray-100 p-5 border-l-4 ${border}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <Icon size={18} className={color} />
                      <h4 className="font-semibold text-gray-700 text-xs">{label}</h4>
                    </div>
                    <p className="text-3xl font-semibold text-gray-600">{total || 0}</p>
                    <div className="flex gap-2 mt-2 flex-wrap">
                      <span className="text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded-full">✓ {pub || 0}</span>
                      <span className="text-xs bg-gray-50 text-gray-500 px-2 py-0.5 rounded-full">○ {(total || 0) - (pub || 0)}</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                <div className="bg-white rounded-sm shadow-sm border border-gray-100 p-6">
                  <h3 className="font-semibold text-gray-500 mb-5">Activity Over Time</h3>
                  <ResponsiveContainer width="100%" height={260}>
                    <AreaChart data={prepareAreaChartData()} margin={{ top: 5, right: 10, left: 0, bottom: 60 }}>
                      <defs>
                        {[['dry','#F97316'],['tank','#3B82F6'],['hire','#10B981'],['ei','#8B5CF6']].map(([k,c]) => (
                          <linearGradient key={k} id={`ag${k}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={c} stopOpacity={0.15}/>
                            <stop offset="95%" stopColor={c} stopOpacity={0}/>
                          </linearGradient>
                        ))}
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} angle={-40} textAnchor="end" height={70} tickLine={false} />
                      <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend iconType="circle" iconSize={8} />
                      <Area type="monotone" dataKey="Dry Box Survey" stroke="#F97316" strokeWidth={2} fill="url(#agdry)" dot={{ r: 3, fill: '#F97316' }} activeDot={{ r: 5 }} connectNulls={true} />
                      <Area type="monotone" dataKey="Tank Cleaning" stroke="#3B82F6" strokeWidth={2} fill="url(#agtank)" dot={{ r: 3, fill: '#3B82F6' }} activeDot={{ r: 5 }} connectNulls={true} />
                      <Area type="monotone" dataKey="Onhire" stroke="#10B981" strokeWidth={2} fill="url(#aghire)" dot={{ r: 3, fill: '#10B981' }} activeDot={{ r: 5 }} connectNulls={true} />
                      <Area type="monotone" dataKey="Ei Receipt" stroke="#8B5CF6" strokeWidth={2} fill="url(#agei)" dot={{ r: 3, fill: '#8B5CF6' }} activeDot={{ r: 5 }} connectNulls={true} />
                      <Area type="monotone" dataKey="TI Report" stroke="#14B8A6" strokeWidth={2} fill="url(#agti)" dot={{ r: 3, fill: '#14B8A6' }} activeDot={{ r: 5 }} connectNulls={true} />

                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                <div className="bg-white rounded-sm shadow-sm border border-gray-100 p-6">
                  <h3 className="font-semibold text-gray-500 mb-5">Record Type Distribution</h3>
                  <ResponsiveContainer width="100%" height={170}>
                    <PieChart>
                      <Pie data={prepareRecordTypesChart()} cx="50%" cy="50%" innerRadius={45} outerRadius={75} paddingAngle={3} dataKey="value">
                        {prepareRecordTypesChart().map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="grid grid-cols-2 gap-2 mt-4">
                    {prepareRecordTypesChart().map((d, i) => (
                      <div key={d.name} className="flex items-center gap-2 text-sm">
                        <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: COLORS[i] }} />
                        <span className="text-gray-600 truncate">{d.name}</span>
                        <span className="font-bold text-gray-800 ml-auto">{d.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

            </main>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AccountantDashboard
